<template>
  <v-dialog
    :value="open"
    max-width="640"
    overlay-color="neutral lighten-1"
    overlay-opacity=".80"
    content-class="qr-dialog"
    scrollable
    @input="cancel"
  >
    <v-card id="checkout" rounded class="pa-6" data-test="payment-update-modal">
      <!-- Title -->
      <v-card-title class="px-0 pt-0 pb-1">
        <h4 v-show="step < 3" class="text-h4">
          {{ currentTitle }}
        </h4>
        <v-spacer />
        <v-chip label small class="chip--badge lapis--text text--lighten-1">
          {{ step }}
        </v-chip>
      </v-card-title>
      <ValidationObserver ref="observer" slim>
        <v-card-text class="px-0">
          <v-stepper v-model="step" elevation="0">
            <v-stepper-items>
              <div id="payment-element-container">
                <v-stepper-content class="pa-0" step="1">
                  <!-- Subtitle description -->
                  <v-card-subtitle class="px-0 pt-0 pb-4">
                    {{ $t('fields.account.billing.payment_edit.desc') }}
                  </v-card-subtitle>

                  <PaymentElement
                    ref="paymentElement"
                    @payment-element-ready="onPaymentElementReady"
                    @switch-to-bank-transfer="setBankTransferSelected(true)"
                  />
                </v-stepper-content>
                <v-stepper-content class="pa-0" step="2">
                  <v-row justify="center">
                    <v-col md="11">
                      <div class="checkout__step-description">
                        <div class="mb-4 d-flex">
                          <Icon name="info-system" outline :size="24" />
                          {{ $t('fields.checkout.form.invoice_info_description') }}
                        </div>
                      </div>

                      <v-btn
                        text
                        :ripple="false"
                        color="primary"
                        data-test="payment-update-bank-transfer-back"
                        @click="setBankTransferSelected(false)"
                      >
                        <span class="navy--text d-flex align-center">
                          <Icon name="arrow-system" outline class="back-arrow-icon mr-1" :size="16" />
                          {{ $t('fields.checkout.form.back_to_instant') }}
                        </span>
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-stepper-content>

                <v-card-actions class="px-0 py-0 mt-3">
                  <v-spacer />
                  <v-btn
                    outlined
                    large
                    :ripple="false"
                    rounded
                    color="primary"
                    data-test="payment-update-button-cancel"
                    @click="cancel"
                  >
                    {{ $t('fields.account.billing.payment_create.btn_outline') }}
                  </v-btn>
                  <v-btn
                    depressed
                    large
                    :ripple="false"
                    rounded
                    color="primary"
                    class="ml-4"
                    data-test="payment-update-button-confirm"
                    :loading="isSaving"
                    :disabled="!isSubmitPossible()"
                    @click="onPaymentMethodSaveV2"
                  >
                    {{ $t('fields.account.billing.payment_create.btn_primary_add') }}
                  </v-btn>
                </v-card-actions>
              </div>
            </v-stepper-items>
          </v-stepper>
        </v-card-text>
      </ValidationObserver>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import type { SavedPaymentMethod } from '@/classes/stripe';
import VeeValidateRefs from '@/Models/VeeValidateRefs';
import Authenticator from '@/Services/Auth/Authenticator';
import StripeCheckoutService from '@/Services/Subscription/StripeCheckoutService';
import { User } from '@/classes/auth';
import Icon from '@/components/Icons/Icon.vue';
import store from '@/store';
import { ValidationObserver } from 'vee-validate';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import OptimizelyService from '@/Services/OptimizelyService';
import Country from '@/Models/Static/Country';
import countries from '@/Models/Static/countries.json';
import PaymentElement from '@/components/PaymentForm/PaymentElement.vue';
import axios from 'axios';
import AppConfig from '@/AppConfig';
import * as Sentry from '@sentry/vue';

@Component({
  components: {
    PaymentElement,
    Icon,
    ValidationObserver,
  },
})
export default class PaymentMethodEdit extends Vue {
  @Prop(Boolean)
  open!: boolean;

  @Prop({ default: undefined })
  wizardStep: any;

  @Prop({ default: undefined })
  redirectStatus: any;

  // region DATA
  // mapState
  user: User = Authenticator.getUser();

  step = 1;
  // Prevents double click issues and shows state of saving
  isSaving = false;
  // Handles business logic and some of the form state
  stripeCheckoutService: StripeCheckoutService = new StripeCheckoutService();

  paymentElementReady = false;

  isBankTransferSelected: boolean = false;

  $refs!: {
    // Used for validation of the form data<ValidationObserver slim ref="observer">
    observer: VeeValidateRefs;
    paymentElement: PaymentElement;
  };

  // region METHODS
  cancel(): void {
    this.$emit('cancel');
    OptimizelyService.trackEvent('payment_method_modal_closed_billing_page');
    this.paymentElementReady = false;
    this.setBankTransferSelected(false);
    setTimeout(() => {
      this.step = 1;
    }, 500);
  }

  async onPaymentMethodSaveV2(): Promise<void> {
    if (!this.isSubmitPossible()) {
      return;
    }
    this.isSaving = true;
    let paymentMethodPayload: any = {
      legal_entity: store.state.auth.team.legal_entity,
      customer: this.stripeCheckoutService.customer,
    };

    try {
      if (!this.isBankTransferSelected) {
        // Get the PaymentElement component reference
        const paymentElementComponent = this.$refs.paymentElement;
        if (!paymentElementComponent || !paymentElementComponent.stripe || !paymentElementComponent.elements) {
          throw new Error('Payment element not ready');
        }

        // Hide the payment element and show loader during processing
        paymentElementComponent.setIsReady(false);

        // Pass billing address to the confirmSetup, so AVS checks can be done by Stripe
        const billingAddress = this.stripeCheckoutService.customer?.address;
        const selectedPaymentMethodType = paymentElementComponent.paymentMethodType;

        // Pix saves a recurring mandate (Pix Automático) instead of charging now, so the
        // SetupIntent confirmation must carry the customer's mandate acceptance, inferred
        // from the consent shown in the Payment Element.
        const pixMandateData = {
          customer_acceptance: {
            type: 'online' as const,
            online: { infer_from_client: true },
          },
        };

        // Confirm the SetupIntent using Stripe Payment Element
        const { error, setupIntent } = await paymentElementComponent.stripe.confirmSetup({
          elements: paymentElementComponent.elements,
          confirmParams: {
            return_url: `${AppConfig.getAppUrl()}/processing?returningFromPaypal=true`,
            payment_method_data: {
              billing_details: {
                name: this.stripeCheckoutService.customer?.name,
                email: this.stripeCheckoutService.customer?.email,
                address: {
                  line1: billingAddress?.line1,
                  line2: billingAddress?.line2,
                  city: billingAddress?.city,
                  state: billingAddress?.state,
                  postal_code: billingAddress?.postal_code,
                  country: billingAddress?.country,
                },
              },
            },
            ...(selectedPaymentMethodType === 'pix' ? { mandate_data: pixMandateData } : {}),
          },
          redirect: 'if_required',
        });

        if (error) {
          // Check if card is reported lost or stolen and force-logout the user
          if (
            (error.type === 'card_error' || error.type === 'validation_error') &&
            error.decline_code &&
            ['lost_card', 'stolen_card'].includes(error.decline_code)
          ) {
            await Authenticator.logout();
          }
          throw new Error(error.message || 'Payment confirmation failed');
        }

        if (!setupIntent || setupIntent.status !== 'succeeded') {
          throw new Error('SetupIntent did not succeed');
        }

        paymentMethodPayload.new_payment_method_id = setupIntent.payment_method;
      }

      // Save the payment method using the confirmed SetupIntent
      await this.stripeCheckoutService.updateCustomerVatRate();

      const response = await axios.post(
        AppConfig.getAPIBaseUrl() + '/subscription/stripe/payment-methods',
        paymentMethodPayload
      );

      if (response.status === 200) {
        // Update team state
        let team = store.state.auth.team;
        team.is_payment_method_invoice = this.isBankTransferSelected;
        store.dispatch('auth/updateTeam', team);

        // Emit events. The store endpoint returns the newly added payment method
        // so the list can append it without re-fetching.
        this.$emit('update-payment-method', this.extractAddedPaymentMethod(response.data));
        this.$emit('update-billing-address');
        this.$emit('cancel');
        this.paymentElementReady = false;

        // Show success message
        await store.dispatch('layout/setSnackbar', {
          type: 'info',
          icon: 'info-system',
          text: this.$t('fields.account.billing.payment_edit.save_ok'),
          isSnackbar: true,
          active: true,
        });
      } else {
        throw new Error('Failed to save payment method');
      }
    } catch (error: unknown) {
      const isDuplicatePaymentMethod = axios.isAxiosError(error) && error.response?.status === 409;

      if (!isDuplicatePaymentMethod) {
        Sentry.captureException(error);
      }

      // A consumed or failed SetupIntent cannot be confirmed again — create a fresh one.
      if (!this.isBankTransferSelected && this.$refs.paymentElement) {
        this.paymentElementReady = false;
        await this.$refs.paymentElement.reinitialize();
      }

      await this.$store.dispatch('layout/setSnackbar', {
        type: 'error',
        icon: 'forbidden-system',
        text: this.getPaymentMethodSaveErrorMessage(error),
        isSnackbar: true,
        active: true,
      });
    } finally {
      this.isSaving = false;
      this.step = 1;
    }
  }

  /**
   * Maps a save failure to a user-facing message: a dedicated copy for the
   * duplicate (409) case, the error's own message when present, otherwise the
   * generic invalid-form fallback.
   */
  private getPaymentMethodSaveErrorMessage(error: unknown): string {
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      return this.$t('fields.account.billing.payment_edit.save_error_duplicate').toString();
    }

    if (error instanceof Error && error.message) {
      return error.message;
    }

    return this.$t('fields.checkout.form.invalid').toString();
  }

  /**
   * Reads the newly added payment method from the add response. The API's
   * Fractal item serializer returns it flat (no `data` wrapper), but we also
   * tolerate a wrapped `{ data: {...} }` shape. Returns null when the response
   * doesn't carry a usable method (e.g. async/redirect flows).
   */
  private extractAddedPaymentMethod(responseData: unknown): SavedPaymentMethod | null {
    if (!responseData || typeof responseData !== 'object' || Array.isArray(responseData)) {
      return null;
    }

    const record = responseData as Record<string, any>;
    const method = record.id ? record : record.data;

    return method?.id ? (method as SavedPaymentMethod) : null;
  }

  // We fill the form for the customer to ease the pain of filling out
  handlePrefillData(): void {
    // Init customer data for prefilling the form
    this.stripeCheckoutService.initCustomerData();
  }

  // No saving process going on and stripe form is ready?
  isSubmitPossible(): boolean {
    return !this.isSaving && this.paymentElementReady;
  }

  setBankTransferSelected(isSelected: boolean): void {
    this.isBankTransferSelected = isSelected;
    this.$store.commit('subscription/setIsBankTransferSelected', isSelected);
    this.step = isSelected ? 2 : 1;
    if (isSelected) {
      OptimizelyService.trackEvent('payment_method_modal_continue_billing_page');
    }
  }
  // endregion METHODS END

  // region COMPUTED METHODS
  get currentTitle(): string {
    switch (this.step) {
      case 1:
        return this.$t('fields.account.billing.payment_create.title_one').toString();
      case 2:
        if (this.isBankTransferSelected) {
          return this.$t('fields.checkout.form.invoice_payment').toString();
        }
        return this.$t('fields.account.billing.payment_create.title_two').toString();
    }
    return '';
  }
  // endregion COMPUTED METHODS EN

  // region WATCHERS
  @Watch('open')
  async onOpenChange(open: boolean): Promise<void> {
    if (!open) {
      return;
    }

    if (this.$refs.paymentElement) {
      this.setBankTransferSelected(false);
      await this.$refs.paymentElement.reinitialize();
    }
  }

  onPaymentElementReady(): void {
    this.paymentElementReady = true;
  }
  // endregion WATCHERS END

  // region HOOKS
  // Mount the payment form, we do this here because refs is not available in created()!
  // Setup the components needed data, like customer data.
  async created(): Promise<void> {
    this.handlePrefillData();

    if (this.wizardStep && parseInt(this.wizardStep) === 3) {
      this.open = true;
      this.wizardStep = undefined;
      this.$emit('update-billing-address');
      this.$emit('cancel');
      this.isSaving = false;

      if (this.redirectStatus !== 'failed') {
        await store.dispatch('layout/setSnackbar', {
          type: 'info',
          icon: 'info-system',
          text: this.$t('fields.account.billing.payment_edit.save_ok'),
          isSnackbar: true,
          active: true,
        });
      } else {
        await store.dispatch('layout/setSnackbar', {
          type: 'error',
          icon: 'forbidden-system',
          text: this.$t('fields.account.billing.payment_edit.save_error_paypal').toString(),
          isSnackbar: true,
          active: true,
        });
      }
    }
  }
  // endregion HOOKS END

  getUserCountryForPaymentMethods(): string {
    const team = Authenticator.getTeam();
    if (!team) {
      return '';
    }
    const countryFromRegistration = team.country_from_registration;
    let result: Country | undefined;
    if (!countryFromRegistration) {
      return '';
    }
    result = countries.find((item: Country) => item.country_iso2 === countryFromRegistration);
    return result ? result.country_iso2 : '';
  }
}
</script>

<style lang="scss"></style>
